import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Handler para o callback do Emergent Google Auth.
 * URL chega como /auth/callback#session_id=XXXX
 *
 * Modo dual:
 *  - Login/Signup (default): chama POST /api/auth/google/exchange e loga.
 *  - Vinculação ao perfil: se localStorage.cm_google_link_pending=1, chama
 *    POST /api/auth/google/link e volta para /perfil.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const { loginWithGoogleSession, setUser } = useAuth();
  const nav = useNavigate();
  const hasProcessed = useRef(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : "";

    let linkPending = false;
    try {
      linkPending = localStorage.getItem("cm_google_link_pending") === "1";
    } catch {
      // ignore
    }

    (async () => {
      if (!sessionId) {
        setErr("Sessão Google ausente");
        if (linkPending) {
          try { localStorage.removeItem("cm_google_link_pending"); } catch { /* ignore */ }
          try {
            const r = await api.get("/auth/me");
            setUser(r.data);
          } catch {
            setUser(false);
          }
        }
        setTimeout(() => nav(linkPending ? "/perfil" : "/login", { replace: true }), 1500);
        return;
      }
      try {
        if (linkPending) {
          // Vincula Google ao usuário autenticado.
          try {
            await api.post("/auth/google/link", { session_id: sessionId });
            toast.success("Conta Google conectada com sucesso");
          } catch (e) {
            const msg = formatApiError(e.response?.data?.detail) || e.message;
            toast.error(msg);
            setErr(msg);
          } finally {
            try {
              localStorage.removeItem("cm_google_link_pending");
            } catch {
              // ignore
            }
            // CRITICAL: AuthProvider pulou o /auth/me por causa do hash session_id;
            // precisamos restaurar o user state antes de navegar pra rota protegida.
            try {
              const r = await api.get("/auth/me");
              setUser(r.data);
            } catch {
              setUser(false);
            }
            window.history.replaceState({}, "", window.location.pathname);
            nav("/perfil", { replace: true });
          }
          return;
        }
        const u = await loginWithGoogleSession(sessionId);
        window.history.replaceState({}, "", window.location.pathname);
        toast.success(`Bem-vindo(a), ${u.name}`);
        const isCollaborator = u.role === "collaborator";
        nav(isCollaborator ? "/mobile" : "/", { replace: true });
      } catch (e) {
        const msg = formatApiError(e.response?.data?.detail) || e.message || "Falha ao autenticar com Google";
        setErr(msg);
        setTimeout(() => nav("/login", { replace: true }), 2500);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginWithGoogleSession, nav]);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-50 p-6"
      data-testid="auth-callback"
    >
      <div className="max-w-sm text-center">
        {err ? (
          <div className="text-red-600 text-sm font-semibold">{err}</div>
        ) : (
          <>
            <Loader2 className="animate-spin text-blue-600 mx-auto mb-3" size={28} />
            <p className="text-slate-700 text-sm font-medium">
              Finalizando login com Google…
            </p>
          </>
        )}
      </div>
    </div>
  );
}
